export type PatientDetailLocationState = {
  source?: string;
  from?: string;
  patientId?: number;
};

export type PatientDetailBackTarget =
  | { kind: "path"; to: string }
  | { kind: "history" };

/**
 * The patient id segment when `path` is a patient detail chrome route (Agenda
 * or Past Visits), else `null`. Normalizes away query/hash and a trailing
 * slash first, so the same patient spelled differently compares equal.
 *
 * Route *shape* alone can't tell an Agenda ↔ Past Visits tab switch apart from
 * a jump to a different patient's chart — both match. Callers that care about
 * patient identity (the recording leave guard) must compare this id, not just
 * `isPatientDetailChromePath`.
 */
export function patientDetailChromePatientId(path: string): string | null {
  const pathname = path.trim().split(/[?#]/)[0] ?? "";
  return /^\/patients\/([^/]+)(?:\/visits)?\/?$/.exec(pathname)?.[1] ?? null;
}

/**
 * True when `path` is a patient detail chrome route (Agenda or Past Visits).
 * Tab switches must not become Back targets.
 */
export function isPatientDetailChromePath(path: string): boolean {
  return patientDetailChromePatientId(path) !== null;
}

/**
 * Resolve where Back should go from patient detail.
 * Prefer explicit external `from` provenance, then browser history, then Patients.
 * Same-chrome tab paths are ignored so Agenda ↔ Past Visits never traps Back.
 */
export function resolvePatientDetailBackTarget(input: {
  state: PatientDetailLocationState | null | undefined;
  historyIndex: number | undefined;
  fallbackPath: string;
}): PatientDetailBackTarget {
  const from = input.state?.from?.trim();
  if (from && !isPatientDetailChromePath(from)) {
    return { kind: "path", to: from };
  }

  if (input.historyIndex != null && input.historyIndex > 0) {
    return { kind: "history" };
  }

  return { kind: "path", to: input.fallbackPath };
}
